// frontend/src/ops/LsmWatcher.jsx
// Ops Cockpit widget for the LSM Smart Watcher (Stream 2 Phase 2.C').
// Consumes /api/admin/lsm-watcher/{status,watches,sync-now,reset-circuit-break,backfill-rt}.
//
// 🔴 UI2 Teil 10, G13a-2 (2026-10-24) — die Fläche ist auf die Listen-Familie
// umgestellt: vier KPI-Kacheln (`KpiRow`/`StatTile`) statt sieben `StatCard`,
// fünf Quick-Filter mit Zählern (`FilterPillRow`, `alwaysShow`), `filter` und
// `page` in der Adresse (`useListQuery`), `TableViewport` gegen den
// Seitenüberlauf, reduzierte Zeilen mobil (`MobileRecordCard`), 50 je Seite,
// drei Leerzustände (`EmptyState` mit `reason`) und der Kappungshinweis.
//
// Die zwei Zähler, die KEINE Kachel mehr tragen (Stalled, Terminal), haben
// ihren Ort als Pille mit Zahl — der Drill-in, der am 2026-08-11 fehlte, ist
// damit für alle fünf Filter derselbe Weg.
//
// 🔴 „Terminal" faltet ZWEI Gründe: `terminal` (LSM-Entität erledigt) und
// `case_terminal` (Fall in Forge geschlossen). Die Pille heisst deshalb
// „Terminal (done or case closed)" und ihr Hinweis sagt, was sie enthält; der
// Client-Filter prüft BEIDE Gründe, sonst zeigte die Pille 120 und die Liste 97.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  Alert, Box, Button, Chip, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography, useMediaQuery, useTheme, Link as MuiLink,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { v2Tone, v2ToneHover } from '../theme/theme'
import PageContainer from '../components/common/PageContainer'
import ViewHeader from '../components/ViewHeader'
import Panel from '../components/common/Panel'
import KpiRow from '../components/common/KpiRow'
import StatTile from '../components/common/StatTile'
import ListToolbar from '../components/common/ListToolbar'
import FilterPillRow from '../components/common/FilterPillRow'
import TableViewport from '../components/common/TableViewport'
import TableFooterSimple from '../components/common/TableFooterSimple'
import MobileRecordCard from '../components/common/MobileRecordCard'
import EmptyState from '../components/common/EmptyState'
import useListQuery from '../components/common/listQuery/useListQuery'
import useColumnPriority from '../components/common/useColumnPriority'
import useContainerWidth from '../components/common/useContainerWidth'
import TableSkeleton from '../Skeletons/TableSkeleton'
import { formatDate } from '../utils/format'
import { CONTENT_WIDTH, COLUMN_MAX, FS, SP } from '../theme/tokens'

const API = process.env.REACT_APP_API_BASE_URL || ''
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || ''
// Mirror the X-Admin-Key pattern used in sibling admin UIs (admin_v2/JobsList.jsx,
// AdminConfigPage.jsx, etc.) — backend lsm_watcher_api endpoints now enforce
// _require_admin_key() per the canonical admin guard from backend/admin_v2/jobs.py.
const ADMIN_HEADERS = ADMIN_KEY ? { 'X-Admin-Key': ADMIN_KEY } : {}

const ENTITY_LABELS = {
  rma_rt: 'RMA — return (RT)',
  rma_dn: 'RMA — replacement (DN)',
  po_avis: 'PO — goods receipt (AVIS)',
}

/** Die fünf Quick-Filter. Kein „All": kein Wert heisst keine Einschränkung. */
const QUICK_VALUES = ['overdue', 'backoff', 'circuit', 'stalled', 'terminal']

const QUICK_LABEL = {
  overdue: 'Overdue',
  backoff: 'In backoff',
  circuit: 'Disabled (recoverable)',
  stalled: 'Stalled (non-return)',
  terminal: 'Terminal (done or case closed)',
}

// Die disabled_reason-Werte, die POST /reset-circuit-break annimmt
// (backend/lsm_watcher_api.py). Bis 2026-08-13 pruefte der Reset-Knopf nur auf
// 'circuit_break' — die API akzeptierte 'lsm_html_error' aber schon vorher.
// Folge: die 12 Watches, die die LSM-Wartung am 11.08. abgeschaltet hat, waren
// ueber die Oberflaeche gar nicht wiederherstellbar; der Owner musste sie per
// SQL reaktivieren. 'entity_gone' kommt neu dazu, weil die Pruefung bei jedem
// Poll erneut laeuft und ein irrtuemlicher Reset sich selbst korrigiert.
const RESETTABLE = ['circuit_break', 'lsm_html_error', 'entity_gone']

/**
 * Die Gründe, die auf der Retourenbahn liegen (`RETURN_LANE_CASE_TYPES` im
 * Backend). Ein `rma_rt`-Watch auf einem Fall AUSSERHALB dieser Menge kann
 * seinen Fall nie bewegen — der Server zählt ihn als `stalled_lane`.
 */
const RETURN_LANE = ['return', 'widerruf']

const SEITE = 50
const KAPPUNG = 200

/**
 * Die Spalten der Tabelle — Deskriptoren für `useColumnPriority`.
 *
 * 🔴 G13a-2 Nachbesserung 4 (Regel 15 W3 / Regel 16 W2, 2026-10-24): die frühere
 * optionale Spalte „Last state" (`xl`) ist ENTFALLEN. Der rohe Zustand steht
 * jetzt als zweite, kleine Zeile in der „Next poll"-Zelle — Hausform der
 * Fristzelle in `rma/RMAFaelligkeit` (Wert oben, Erläuterung als `caption`
 * darunter). Damit ist er auf JEDER Breite ohne Zeigegerät erreichbar: der
 * Tooltip davor öffnete nur mit der Maus, auf Touch-Geräten gar nicht
 * (WCAG 2.1.1). Eine eigene Spalte hätte bei 1440 ohnehin nicht gepasst
 * (Kernbudget 6 Spalten / 1400 px gegen 1064 px Behälter) und bei ≥1920
 * denselben Wert ein zweites Mal gezeigt.
 *
 * 🔴 G13a-2 Nachbesserung 5 (Regel 16 C2 / W-1, 2026-10-25): das Etikett
 * „Last state" stand als TEXT in der Zelle und brach sie in den 140 − 2 × 16 =
 * **108 px** Inhalt auf eine eigene Zeile. Das Etikett steht jetzt im
 * **Spaltenkopf**.
 *
 * 🔴 G13a-2 Nachbesserung 6 (Regel 16 C3, 2026-10-25): „einzeilig" war nur für
 * eine Fixture-Form belegt, die kein Erzeuger schreibt (der alte
 * Fixture-Schlüssel ist in `LsmWatcher.test.jsx` benannt). Die echten Formen
 * (RT, DN, AVIS — s. `stateText`) sind zusammen breiter als die Zelle. Die
 * `caption` kürzt deshalb mit **Ellipse** (`noWrap`) statt umzubrechen — der
 * Textinhalt bleibt vollständig, nur die Anzeige ist beschnitten; die
 * Begründung samt der verworfenen `aria-label`-Variante steht an `stateText`.
 *
 * 🔴 G13a-2 Nachbesserung 7 (2026-10-26): **das Spaltenbudget ist neu
 * verteilt.** Die Ellipse war als Notbremse gedacht, wurde aber zur Regel:
 * bei 1440 mass die Entitätsspalte **392 px** (für „RMA — return (RT)",
 * ≈ 115 px Text), die Zustandsspalte hatte 108 px Inhalt, und JEDE Zeile las
 * „booking=BOOKED · …" — `process` und `quality` waren unsichtbar, alle Zeilen
 * sahen gleich aus (Foto `g13a2e_zweig/1440_grund.png`, Tabellenbreite
 * 1112 px). Neu — 🔴 **Stand Nachbesserung 7, also VOR Nachbesserung 8 und
 * damit überholt**: die Kennung stand hier noch auf 108. Das gültige Budget
 * steht an `zustandBreite` und in der zweiten Tabelle (Nachbesserung 8):
 *
 * | Spalte | 1440 (Behälter 1112) | 1280 (Behälter ≈ 952) |
 * |---|---|---|
 * | Entity | 250 (Rest) | 206 (Rest) |
 * | Identifier | 108 | 108 |
 * | Next poll / last state | **348** | **232** |
 * | Polls / errors | 120 | 120 |
 * | Status | 136 | 136 |
 * | Actions | 150 | 150 |
 *
 * Die Rechnung je Spalte steht an `zustandBreite`; die Ellipse bleibt die
 * Notbremse für **unbekannte** Schlüssel, nicht für die drei echten Formen.
 *
 * 🔴 G13a-2 Nachbesserung 8 (2026-10-27): **die Kennung brach in JEDER Zeile
 * um.** Foto `g13a2f_zweig/1440_grund.png` gegen `g13a2f_basis/1440_grund.png`:
 * die 108-px-Zelle hat 76 px Inhalt, die Kennung braucht 91 px. Und sie brach
 * nicht an der Breite, sondern an der TRENNSTELLE — der Bindestrich ist im
 * Zeilenumbruch (UAX #14, Klasse HY) eine erlaubte Stelle, deshalb standen
 * „RT-2026-" und „0001" untereinander. Die **echten** Kennungen (Prod-Messung
 * 26.10.2026, `lsm_watches`: 175 von 175 Zeilen) haben genau 12 Zeichen in der
 * Form `RT-999999999` (112), `IN-999999999` (37, Avis) und `DN-999999999` (26):
 * nur EIN Bindestrich, also genau eine Trennstelle, direkt nach dem Präfix.
 *
 * **Rechnung (Zeichenbreite, nicht geschätzt):** `<code>` erbt die Zellschrift
 * (MUI `MuiTableCell` root = `typography.body2` = 0,875 rem bei
 * `html { font-size: 14px }` = **12,25 px**) und rendert in der
 * Browser-Monospace; die UA-Vorgabe setzt für `code` nur `font-family`, keine
 * Größe. Am Foto gemessen (Abstand der Glyphen-Anfänge in „RT-2026-":
 * 550, 558, 565, 573, 580, 588, 595, 603) sind das **7,571 px Vorschub je
 * Zeichen** (0,618 em). 12 Zeichen = **90,9 px**; mit 2 × 16 px Zellpolster
 * braucht die Spalte **122,9 px** → gewählt **126** (94 px Inhalt, 3,1 px
 * Luft). 🔴 Die nominale Hausrechnung (0,6 em = 7,35 px) hätte 120,2 px
 * ergeben; gerechnet wird mit dem GEMESSENEN Vorschub, sonst bricht die Zelle
 * bei 124 px wieder.
 *
 * Bezahlt wird aus der Entität (250 → 232 / 206 → 188); die Zustandsspalte
 * bleibt bei 348/232:
 *
 * | Spalte | 1440 (Behälter 1112) | 1280 (Behälter ≈ 952) |
 * |---|---|---|
 * | Entity | **232** (Rest) | **188** (Rest) |
 * | Identifier | **126** | **126** |
 * | Next poll / last state | 348 | 232 |
 * | Polls / errors | 120 | 120 |
 * | Status | 136 | 136 |
 * | Actions | 150 | 150 |
 *
 * Die längste Entity-Bezeichnung ist
 * `PO — goods receipt (AVIS)` (26 Zeichen, Roboto `body2`): 11,92 em =
 * **146 px**; bei 1280 bleiben 156 px Inhalt (am Foto gemessen sind es rund
 * 3 px weniger, also 153) — sie steht einzeilig. Die Entity-Zelle trägt
 * bewusst KEIN `nowrap`: sie hat die Breite, die ihre Bezeichnung braucht, und
 * wenn sie sie einmal nicht mehr hätte, soll der Text UMBRECHEN statt in die
 * Nachbarspalte zu laufen — genau das prüft `G13a-2/8` über die Zeilenboxen.
 * Das `nowrap` gehört an die Kennungszelle (s. `KENNUNG_SX`), weil deren echte
 * Form ihre einzige Trennstelle nach `RT-` hat.
 *
 * `actions` 130 → 150 (Regel 16 W-3, Nachbesserung 6): „Open purchase order"
 * braucht als Knopf rund 140 px und endete auf 1440 mitten im Wort an der
 * Panelkante. 150 liegt im Rahmen von `COLUMN_MAX.number.max` (160), und das
 * Breitenbudget rechnet über die Maxima, nicht über `width` — die
 * Spaltenauswahl ändert sich dadurch nicht.
 */
const SPALTEN = [
  { key: 'entity', priority: 'kern', kind: 'identity' },
  // 126 statt 108 (Nachbesserung 8): 12 Festbreitenzeichen = 90,9 px, plus
  // 2 × 16 px Polster = 122,9 px. Die Rechnung steht im Docstring oben.
  { key: 'identifier', priority: 'kern', kind: 'identity', width: 126 },
  // `nextpoll` trägt KEINE feste Breite: sie hängt an der gemessenen
  // Behälterbreite (`zustandBreite`), weil die Zustandszeile die einzige Zelle
  // ist, deren Inhalt eine Mindestbreite VERLANGT. `useColumnPriority` liest
  // `width` ohnehin nicht (es rechnet über `COLUMN_MAX[kind].max`), die
  // Spaltenauswahl bleibt von dieser Zeile unberührt.
  { key: 'nextpoll', priority: 'kern', kind: 'date' },
  { key: 'polls', priority: 'kern', kind: 'number', width: 120 },
  { key: 'status', priority: 'kern', kind: 'chip', width: 136 },
  { key: 'actions', priority: 'kern', kind: 'number', width: 150 },
]

const SPALTE_VON = Object.fromEntries(SPALTEN.map((s) => [s.key, s]))

function spaltenBreite(key) {
  const sp = SPALTE_VON[key]
  return sp?.width ? { width: sp.width } : undefined
}

/** Die Kennungszelle: feste Breite UND `nowrap`.
 *
 *  🔴 G13a-2 Nachbesserung 8 (2026-10-27): die Breite allein genügt NICHT.
 *  Eine Kennung ist ein Wort, kein Fliesstext — der Browser darf sie nicht
 *  trennen. Er tat es trotzdem, und zwar an der einzigen erlaubten Stelle:
 *  `RT-999999999` trägt genau eine Trennstelle (UAX #14 Klasse HY, nach dem
 *  Bindestrich), und in 76 px Inhalt passten die 91 px nicht. `nowrap` nimmt
 *  die Trennstelle weg; die 126 px geben dem Text den Platz, den er dann
 *  braucht. Beides zusammen ist die Zusage, keines von beiden allein.
 *
 *  ⚠️ Was `nowrap` NICHT kann: eine Kennung, die länger als 94 px ist, ragte
 *  in die Nachbarspalte statt umzubrechen. Das ist der bewusst gewählte
 *  schlechtere Fall — die Prod-Messung vom 26.10.2026 zeigt 175 von 175
 *  Kennungen mit genau 12 Zeichen. Seit Nachbesserung 9 (2026-10-27) misst
 *  `frontend/tests/listen/lsm-watcher.spec.ts` genau diesen Ueberlauf
 *  (`G13a-2/8`, rechte Kante gegen die Inhaltskante der Zelle), damit er
 *  nicht still bleibt; `G13a-2/8k` ist die Stoerprobe dazu.
 */
const KENNUNG_SX = { ...spaltenBreite('identifier'), whiteSpace: 'nowrap' }

/** Die zwei Breiten der Zustandsspalte und die Zeichenrechnung dahinter.
 *
 *  Gemessen (Foto `g13a2e_zweig/1440_grund.png`): die Tabelle ist bei 1440
 *  **1112 px** breit; die vier festen Spalten belegen **126 + 120 + 136 + 150 =
 *  532 px**, die Entität nimmt den Rest — **232 px** bei 1440, **188 px** bei
 *  1280. (Vor Nachbesserung 8 waren es 108 + 120 + 136 + 150 = 514 px und
 *  damit 250/206 für die Entität; das ist der überholte Stand.) Bei 1280 fehlen
 *  160 px (Sidebar 240 und Seitenrand 24 bleiben gleich), der Behälter ist
 *  ≈ **952 px**.
 *
 *  Die längste ECHTE Zeile ist die RT-Form mit Qualität:
 *
 *      booking=BOOKED · process=FINISHED · quality=A       45 Zeichen
 *
 *  `caption` ist 10,5 px (`0.75rem` bei `htmlFontSize: 14`); die Hausrechnung
 *  `docs/reviews/feat-briefing-preis-pruefer.ux-review.md:216` setzt ≈ 0,56 em
 *  je Zeichen. Großbuchstaben und Ziffern sind breiter, Kleinbuchstaben
 *  schmaler — je Zeichenklasse gerechnet (7/7/7 lowercase, 15 uppercase, 3 ×
 *  `=`, 2 × `·`, 4 Leerzeichen) ergibt ≈ **245 px**. Mit 2 × 16 px Zellpolster
 *  braucht die Spalte ≈ 277 px.
 *
 *      WEIT  348 px → 316 px Inhalt → 71 px Luft   (1440: booking + process + quality)
 *      ENG   232 px → 200 px Inhalt → 10 px Luft   (1280: booking + process)
 *
 *  Der längste Qualitätswert ist nicht `A`, sondern `DEF` (`_WORST_RANK` in
 *  `_fetch_rt`: A/B/C/DEF/LQ) — `quality=DEF` sind 2 Zeichen mehr, ≈ 12 px;
 *  die 71 px Luft bei `WEIT` decken das.
 *
 *  Die Avis-Form mit Datum (`booking=BOOKED · process=FINISHED · booked=16.10.2026`,
 *  53 Zeichen ≈ 303 px) passt damit nur bei `WEIT` vollständig — bei 1280
 *  kostet sie die Ellipse, wie `quality`; das ist die gewollte Abstufung.
 *  `ENG` deckt die 33 Zeichen von `booking=BOOKED · process=FINISHED`
 *  (≈ 190 px) mit 10 px Luft.
 *
 *  1064 ist der Behälterwert, den das Konzept dem 1440-Viewport zuordnet
 *  (§3.2.2:455-460) — er liegt zwischen den beiden gemessenen Behältern
 *  (952 / 1112) und schaltet die Stufe damit genau dort, wo die Breite da ist.
 */
const ZUSTAND_WEIT = 348
const ZUSTAND_ENG = 232
const ZUSTAND_WEIT_AB = 1064

function zustandBreite(behaelter) {
  return behaelter >= ZUSTAND_WEIT_AB ? ZUSTAND_WEIT : ZUSTAND_ENG
}

function fmtRelative(iso) {
  if (!iso) return '—'
  const diff = (new Date(iso).getTime() - Date.now()) / 1000
  const abs = Math.abs(diff)
  const s =
    abs < 60 ? `${Math.round(abs)}s` :
    abs < 3600 ? `${Math.round(abs / 60)}m` :
    abs < 86400 ? `${Math.round(abs / 3600)}h` :
    `${Math.round(abs / 86400)}d`
  return diff >= 0 ? `in ${s}` : `${s} ago`
}

/** Der Link zur Entität einer Zeile — EINE Quelle für Entity-Zelle und
 *  Zeilenaktion (Endzustände, s. `zeilenAktion`). */
function entityHref(w) {
  if (w.entity_type === 'po_avis') return `/purchase_order/${w.entity_id}`
  if (String(w.entity_type || '').startsWith('rma_')) return `/rma/${w.entity_id}`
  return null
}

/** Die Betriebszustände in ANZEIGEREIHENFOLGE.
 *
 *  Die Reihenfolge ist keine Kosmetik: die Zelle kürzt mit Ellipse, also ist
 *  der Anfang der Zeichenkette das, was man ohne Zeigegerät noch liest. Der
 *  Buchungsstatus steht vorn, weil er die Frage beantwortet, wegen der man
 *  hinsieht („ist die Rücksendung im Lager angekommen?"); `process_status`,
 *  `carrier_status` und `quality` folgen, weil sie den Übergang nach `booked`
 *  erklären (`_apply_rt`: BOOKED + FINISHED + Qualität).
 *
 *  🔴 G13a-2 Nachbesserung 7 (2026-10-26): `carrier_status` ist hier
 *  aufgenommen, weil **drei** Erzeuger in `last_state` schreiben, nicht einer
 *  (s. `stateText`). Er steht hinter `process_status` und vor `quality`: er
 *  beschreibt denselben Transportabschnitt wie `process`, nur aus Sicht des
 *  Carriers.
 */
const ZUSTAND_REIHENFOLGE = ['booking_status', 'process_status', 'carrier_status', 'quality']

/** Der Anzeigename je Zustandsschlüssel — EINE Tabelle statt `_status`-Abschnitt.
 *
 *  🔴 G13a-2 Nachbesserung 7: der frühere `k.replace('_status', '')` hätte aus
 *  `booking_date` (Avis) ein **`booking`** gemacht — dieselbe Beschriftung wie
 *  `booking_status`, zwei verschiedene Werte unter einem Namen. Die Tabelle
 *  macht die Ausnahmen sichtbar: `booking_date` heisst **`booked`**, weil
 *  `booking` schon der Status ist.
 */
const ZUSTAND_LABEL = {
  booking_status: 'booking',
  process_status: 'process',
  carrier_status: 'carrier',
  quality: 'quality',
  booking_date: 'booked',
}

/** Zustandsschlüssel, deren Wert ein DATUM ist — er wird formatiert, nicht roh.
 *
 *  `_fetch_avis` (`sync/lsm_watcher.py`) liefert `booking_date` als
 *  **ISO-String** („2026-10-16"), weil der Wert unverändert in JSONB geht
 *  (`date` ist dort nicht serialisierbar, Docstring an `_fetch_avis`). In der
 *  Zelle wäre `booked=2026-10-16` die einzige maschinenlesbare Form zwischen
 *  lauter lesbaren Werten — und die Hausform für ein sichtbares Datum ist
 *  `formatDate` (`dd.MM.yyyy`, `utils/format`). Der ISO-String bleibt im
 *  `last_state` und im Admin-Endpunkt.
 */
const ZUSTAND_DATUM = new Set(['booking_date'])

/** Zustandsschlüssel, die KEIN Betriebszustand sind und nicht angezeigt werden.
 *
 *  🔴 `notiz_vorhanden` (Regel 16 C3, Nachbesserung 6): der Wahrheitswert misst
 *  nur, ob beim Buchungs-Poll schon eine Notiz am Kopf stand — er steuert keinen
 *  Übergang, er ist Buchhaltung des Watchers (`sync/lsm_watcher.py` `_fetch_rt`,
 *  Grill B1: „messen statt raten"). In der Zelle wäre er eine dritte Zeile
 *  Betriebsfremdes. Er bleibt in `last_state` und im Admin-Endpunkt sichtbar.
 */
const ZUSTAND_INTERN = new Set(['notiz_vorhanden'])

/** Der rohe LSM-Zustand als „schlüssel=wert · …".
 *
 *  🔴 G13a-2 Nachbesserung 7 (2026-10-26): **es gibt DREI Erzeuger, nicht
 *  einen.** Bis hierher sagten Docstring, `docs/features/rma.md` und der
 *  Changelog „vier Schlüssel" — das stimmt nur für die Rücksendung:
 *
 *  | Erzeuger (`sync/lsm_watcher.py`) | Schlüssel in `last_state` |
 *  |---|---|
 *  | `_fetch_rt` (RT) | `booking_status`, `process_status`, `quality`, `notiz_vorhanden` |
 *  | `_fetch_dn` (DN) | `booking_status`, `process_status`, `carrier_status` (`None`, solange `tracking_poll.py` den Carrier führt) |
 *  | `_fetch_avis` (AVIS) | `booking_status`, `process_status`, `booking_date` (ISO-Datum, s. `ZUSTAND_DATUM`) |
 *
 *  `_poll_one` entfernt nur `_`-präfixierte Schlüssel und schreibt den Rest roh
 *  in `last_state`. Sichtbar sind je Form alle nicht-leeren Werte
 *  (`ZUSTAND_REIHENFOLGE` + `ZUSTAND_LABEL`); `notiz_vorhanden` fällt als
 *  interne Buchhaltung weg (`ZUSTAND_INTERN`), `carrier_status` und
 *  `booking_date` nur, wenn LSM sie liefert (`null` fällt über den
 *  Wahrheitswert-Filter heraus). Unbekannte Schlüssel bleiben erhalten und
 *  stehen HINTEN — sie werden also zuerst gekürzt, nicht versteckt.
 *
 *  **Einzeilige Zelle (C3b):** die Zeile bleibt einzeilig (`noWrap`), aber die
 *  Spalte ist seit Nachbesserung 7 so breit, dass die drei echten Formen
 *  vollständig stehen (s. `zustandBreite`); die Ellipse ist die **Notbremse**
 *  für unbekannte Schlüssel. Das ist eine Kürzung der ANZEIGE, nicht des
 *  Inhalts: `textContent` bleibt vollständig, und damit auch der zugängliche
 *  Name — ein Screenreader liest die ganze Zeichenkette vor.
 *
 *  🔴 **Die `aria-label`-Variante ist deshalb NICHT gewählt** (Regel 15 W-2
 *  hatte sie als nicht erwogen beanstandet): sie wäre hier wirkungslos, weil
 *  `text-overflow: ellipsis` den Textinhalt unangetastet lässt und der
 *  zugängliche Name eines Textelements daraus entsteht. Ein `aria-label` würde
 *  denselben Wert ein zweites Mal setzen und bei einem künftigen Umbau
 *  auseinanderlaufen; ein `title` wäre wieder ein Träger, der nur mit der Maus
 *  öffnet — genau der Befund aus Nachbesserung 4 (WCAG 2.1.1).
 */
function stateText(w) {
  if (!w.last_state) return '—'
  const rang = (k) => {
    const i = ZUSTAND_REIHENFOLGE.indexOf(k)
    return i < 0 ? ZUSTAND_REIHENFOLGE.length : i
  }
  const wert = (k, v) => (ZUSTAND_DATUM.has(k) ? formatDate(v) : String(v))
  const teile = Object.entries(w.last_state)
    .filter(([k, v]) => v && !ZUSTAND_INTERN.has(k))
    .sort(([a], [b]) => rang(a) - rang(b))
    .map(([k, v]) => `${ZUSTAND_LABEL[k] || k}=${wert(k, v)}`)
  return teile.length ? teile.join(' · ') : '—'
}

/**
 * Ein `/status`-Zähler, wie er ist — und `null`, wenn der Schlüssel FEHLT.
 *
 * 🔴 G13a-2 Nachbesserung 3 (Regel 15 W4). Bis hierher stand `?? 0` an den
 * fünf Zählern: eine ältere Antwort, ein Proxy oder ein Tippfehler zeigte die
 * Pille mit **0** — genau die „erfundene Null", die die Kacheln mit
 * `value={… ?? null}` + `hint="load failed"` vermeiden (P9: „unbekannt" ist
 * nicht 0). Fehlt der Schlüssel, ist der Wert unbekannt, nicht null Stück.
 */
function zahl(v) {
  return typeof v === 'number' ? v : null
}

/** Die Summe mehrerer Zähler — `null`, sobald EINER fehlt (Teilwissen ist
 *  keine Summe). */
function summe(...teile) {
  return teile.every((t) => typeof t === 'number')
    ? teile.reduce((a, b) => a + b, 0)
    : null
}

/** Der Client-Filter EINES Werts. Die Prädikate sind die des Servers. */
function trifft(w, filter, jetzt) {
  switch (filter) {
    case 'overdue':
      return Boolean(w.enabled) && new Date(w.next_poll_at).getTime() <= jetzt
    case 'backoff':
      return Boolean(w.enabled) && (w.consecutive_errors || 0) > 0
    case 'circuit':
      return !w.enabled && RESETTABLE.includes(w.disabled_reason)
    case 'stalled':
      // Dieselbe Verengung wie der Server: `case_type NOT IN ('return','widerruf')`
      // — ein NULL-Fall zählt dort NICHT (SQL-`NOT IN` mit NULL ist NULL), hier
      // deshalb auch nicht. Ohne diese Gleichheit zeigt die Pille eine andere
      // Menge als `stalled_lane`.
      return Boolean(w.enabled)
        && w.entity_type === 'rma_rt'
        && typeof w.case_type === 'string'
        && !RETURN_LANE.includes(w.case_type)
    case 'terminal':
      return !w.enabled
        && (w.disabled_reason === 'terminal' || w.disabled_reason === 'case_terminal')
    default:
      return true
  }
}

export default function LsmWatcher() {
  const theme = useTheme()
  const mobil = useMediaQuery(theme.breakpoints.down('sm'))

  const [status, setStatus] = useState(null)
  const [watches, setWatches] = useState(null)
  const [loading, setLoading] = useState(true)
  // 🔴 G13a-2 Nachbesserung 2: LADE- und AKTIONSfehler sind zwei Zustände.
  // Bis hierher schrieben `fetchAll` und die drei Aktionshandler in dasselbe
  // `error`. Folge auf dem Foto vom 2026-10-24: ein gescheiterter Aufbau stand
  // DREIMAL auf der Seite (Kopf-Alert, Alert im Panel, Fehler-Leerzustand).
  // Jetzt: der Ladefehler steht EINMAL im Panel (Leerzustand mit „Try again",
  // bei vorhandenen Daten als eine Meldung über der Leiste), und der
  // schliessbare Kopf-Alert trägt nur Aktionsfehler und Info.
  const [error, setError] = useState(null)
  const [aktionsFehler, setAktionsFehler] = useState(null)
  const [busy, setBusy] = useState({})
  const [info, setInfo] = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, w] = await Promise.all([
        axios.get(`${API}/api/admin/lsm-watcher/status`, { headers: ADMIN_HEADERS }),
        axios.get(`${API}/api/admin/lsm-watcher/watches`, {
          params: { limit: KAPPUNG }, headers: ADMIN_HEADERS,
        }),
      ])
      setStatus(s.data)
      setWatches(w.data || [])
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // ── Der Zustand der Liste lebt in der ADRESSE (C9-8) ─────────────────────
  // `filter` ist eine `single`-Dimension OHNE Vorgabe und OHNE `all`: kein Wert
  // heisst keine Einschränkung, und „Clear all" schreibt den Grundzustand
  // (Parameter weg). Mit `all` in `values` wäre `?filter=all` ein echter Wert.
  // 🔴 G13a-2 Nachbesserung 4 (Regel 16 W3): die Dimension heisst „Reason", nicht
  // „Filter". Der Chip der aktiven Kriterien baut sein Etikett als
  // `${label}: ${wert}` — mit „Filter" stand dort „Filtered by: Filter: Overdue",
  // also zweimal dasselbe Wort. „Reason" benennt den Sachverhalt: jeder der fünf
  // Werte ist ein Grund, aus dem ein Watch in der Liste steht (überfällig, im
  // Backoff, abgeschaltet, festgefahren, erledigt). „Watch" wäre die Zeilenart
  // und läse sich als „Watch: Overdue" wie eine Anweisung, nicht wie ein Befund.
  const dimensionen = useMemo(() => [{
    key: 'filter',
    kind: 'single',
    label: 'Reason',
    values: QUICK_VALUES.map((v) => ({ value: v, label: QUICK_LABEL[v] })),
  }], [])
  const query = useListQuery({ dimensions: dimensionen, pageSize: SEITE })
  const { state, set, clearAll, chips } = query

  // P7: nach „Clear all" steht der Fokus auf dem ersten Bedienelement von Z1 —
  // hier ist das die erste Quick-Filter-Pille (es gibt kein Suchfeld).
  const quickRef = useRef(null)
  const clearAllMitFokus = useCallback(() => {
    clearAll()
    window.setTimeout(() => {
      const pille = quickRef.current?.querySelector('button[aria-pressed]')
      pille?.focus?.()
    }, 0)
  }, [clearAll])

  // ── Ableitungen ───────────────────────────────────────────────────────────
  const basis = useMemo(() => watches ?? [], [watches])
  const sperre = Boolean(error) && status === null
  // Der Grund, den die vier Kacheln tragen, wenn `/status` nicht geladen wurde.
  // In diesem Zweig heisst `status === null` immer „Aufbau gescheitert" — der
  // ERSTE Aufbau rendert das Skelett (`loading && !status`), nicht die Kacheln.
  // Ohne ihn staende „—" ohne Auskunft da (P9: „unbekannt" ist nicht 0).
  const kpiGrund = status ? null : 'load failed'
  // 🔴 G13a-2 Nachbesserung 3 (Regel 15 W4): fehlt EIN `/status`-Schlüssel, trägt
  // die Kachel „—" MIT Auskunft („count not reported") statt einer erfundenen 0.
  // Der Ladefehler (`kpiGrund`) hat Vorrang — er erklärt alle vier Kacheln.
  const kachelHinweis = (normal, wert) => (
    kpiGrund ?? (wert == null ? 'count not reported' : normal)
  )

  const zaehler = useMemo(() => {
    if (!status) return null
    return {
      overdue: zahl(status.overdue_count),
      backoff: zahl(status.in_error_backoff),
      circuit: summe(
        status.disabled_circuit,
        status.disabled_html_error,
        status.disabled_entity_gone,
      ),
      stalled: zahl(status.stalled_lane),
      terminal: summe(status.disabled_terminal, status.case_terminal),
    }
  }, [status])

  const gefiltert = useMemo(() => {
    if (!state.filter) return basis
    const jetzt = Date.now()
    return basis.filter((w) => trifft(w, state.filter, jetzt))
  }, [basis, state.filter])

  const gesamt = status?.total_watches ?? null
  const gekappt = basis.length === KAPPUNG && (gesamt ?? 0) > KAPPUNG

  // Seite gegen die Trefferzahl klemmen (der Hook kennt sie nicht).
  const seite = Math.min(
    Number(state.page ?? 1),
    Math.max(1, Math.ceil(gefiltert.length / SEITE)),
  )
  const seitenZeilen = gefiltert.slice((seite - 1) * SEITE, seite * SEITE)

  const leerGrund = (() => {
    if (sperre) return 'error'
    if (status === null) return null
    if ((gesamt ?? 0) === 0) return 'no-data'
    if (gefiltert.length === 0) return 'no-match'
    return null
  })()

  // ── Die Breitenmechanik (G0b) ─────────────────────────────────────────────
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.wide ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  // Seit Nachbesserung 4 gibt es keine optionale Spalte mehr: alle sechs
  // Spalten sind `kern`, `visible` speist nur noch das Breitenbudget.
  const spaltenBudget = useMemo(
    () => sichtbareSpalten.reduce((summe, sp) => summe + (COLUMN_MAX[sp.kind]?.max ?? 0), 0),
    [sichtbareSpalten],
  )
  const tabellenMax = spaltenBudget > 0 ? Math.min(messBasis, spaltenBudget) : undefined
  // Die Zustandsspalte ist die EINE Spalte mit einer Mindestbreite aus Inhalt
  // (s. `zustandBreite`); die Entität nimmt den Rest.
  const zustandSpalte = zustandBreite(messBasis)

  // ── Aktionen ──────────────────────────────────────────────────────────────
  const handleSyncNow = useCallback(async (w) => {
    setBusy((p) => ({ ...p, [w.id]: 'sync' }))
    setAktionsFehler(null)
    try {
      await axios.post(`${API}/api/admin/lsm-watcher/sync-now`, { watch_id: w.id }, { headers: ADMIN_HEADERS })
      setInfo(`Boosted ${w.lsm_identifier} — next tick will pick it up within 5 min.`)
      await fetchAll()
    } catch (err) {
      setAktionsFehler(err?.response?.data?.error || err.message)
    } finally {
      setBusy((p) => { const n = { ...p }; delete n[w.id]; return n })
    }
  }, [fetchAll])

  const handleReset = useCallback(async (w) => {
    setBusy((p) => ({ ...p, [w.id]: 'reset' }))
    setAktionsFehler(null)
    try {
      await axios.post(`${API}/api/admin/lsm-watcher/reset-circuit-break`, { watch_id: w.id }, { headers: ADMIN_HEADERS })
      setInfo(`Re-enabled watch ${w.lsm_identifier}.`)
      await fetchAll()
    } catch (err) {
      setAktionsFehler(err?.response?.data?.error || err.message)
    } finally {
      setBusy((p) => { const n = { ...p }; delete n[w.id]; return n })
    }
  }, [fetchAll])

  const handleBackfillRt = useCallback(async () => {
    setBusy((p) => ({ ...p, __backfill: true }))
    setAktionsFehler(null)
    try {
      const { data } = await axios.post(`${API}/api/admin/lsm-watcher/backfill-rt`, {}, { headers: ADMIN_HEADERS })
      setInfo(`RT backfill: considered ${data.considered}, registered/updated ${data.registered_or_updated}.`)
      await fetchAll()
    } catch (err) {
      setAktionsFehler(err?.response?.data?.error || err.message)
    } finally {
      setBusy((p) => { const n = { ...p }; delete n.__backfill; return n })
    }
  }, [fetchAll])

  // ── Bausteine ─────────────────────────────────────────────────────────────
  const statusChip = (w) => (
    w.enabled ? (
      <Chip size="small" label="active" color="success" variant="outlined" />
    ) : (
      /* Nicht nur 'circuit_break': das ist der eine Grund, der ab 2026-08-13 nie
         mehr vergeben wird, waehrend lsm_html_error und entity_gone grau blieben.
         'warning' statt 'error', passend zur Kachel — diese Zeilen sind ueber den
         Reset-Knopf daneben wiederherstellbar. */
      <Chip
        size="small"
        label={w.disabled_reason || 'disabled'}
        color={RESETTABLE.includes(w.disabled_reason) ? 'warning' : 'default'}
        variant="outlined"
      />
    )
  )

  const zeilenAktion = (w) => {
    if (w.enabled) {
      return (
        <Button
          size="small"
          aria-label={`Sync now ${w.lsm_identifier}`}
          onClick={() => handleSyncNow(w)}
          disabled={!!busy[w.id]}
        >
          {busy[w.id] === 'sync' ? '…' : 'Sync now'}
        </Button>
      )
    }
    if (RESETTABLE.includes(w.disabled_reason)) {
      return (
        <Button
          size="small"
          aria-label={`Reset ${w.lsm_identifier}`}
          // K-3: ein Text-Knopf traegt `palette.warning.main` als Schrift auf dem
          // Papier (3,113:1 hell); der v2-Ton traegt in beiden Modi. Der Hover
          // gehoert dazu (Nachbesserung 5, 2026-10-11): ohne ihn faellt MUI's
          // `text`-Hover auf `primary` — blaue Toenung unter warn-farbener Schrift.
          // `v2ToneHover` setzt Rand UND Toenung; der Rand ist hier wirkungslos
          // (MuiButtonBase traegt `border: 0`), die Toenung traegt.
          sx={(t) => ({ color: v2Tone(t, 'warn').main, ...v2ToneHover(t, 'warn') })}
          onClick={() => handleReset(w)}
          disabled={!!busy[w.id]}
        >
          {busy[w.id] === 'reset' ? '…' : 'Reset'}
        </Button>
      )
    }
    /* 🔴 G13a-2 Nachbesserung 2: Endzustände (`terminal`/`case_terminal`) hatten
       GAR KEINE Aktion — die Zelle war leer, und P19 meldete drei Zeilen „ohne
       Zeilenbezug". Der ⋮ ist hier nicht die Hausform: eine Zeile mit GENAU
       EINER Handlung trägt sie inline (`RowActionsMenu` @dontUseFor
       „Single-action rows"). Die eine Handlung, die ein erledigter Watch noch
       hat, ist der Weg in seinen Fall — dieselbe Adresse wie der Link in der
       Entity-Zelle, hier als Knopf mit dem Zeilenbezug im Namen (P19). */
    const href = entityHref(w)
    if (!href) return null
    const wort = w.entity_type === 'po_avis' ? 'purchase order' : 'case'
    return (
      <Button
        size="small"
        aria-label={`Open ${wort} for ${w.lsm_identifier}`}
        onClick={() => window.open(href, '_blank', 'noopener')}
      >
        {`Open ${wort}`}
      </Button>
    )
  }

  const quickOptionen = QUICK_VALUES.map((v) => ({
    value: v,
    label: QUICK_LABEL[v],
    ...(zaehler ? { count: zaehler[v] } : {}),
  }))

  const quickReihe = (
    <Box ref={quickRef}>
      <FilterPillRow
        alwaysShow
        options={quickOptionen}
        value={state.filter ?? null}
        onChange={(v) => set('filter', v)}
        disabled={sperre}
      />
    </Box>
  )

  const leerzustand = leerGrund ? (
    <Box sx={{ py: SP.lg }}>
      <EmptyState
        reason={leerGrund}
        message={
          leerGrund === 'error'
            ? 'Could not load the watcher.'
            : leerGrund === 'no-data'
              ? 'No watches are registered.'
              : 'No watches match this filter.'
        }
        action={
          leerGrund === 'no-match'
            ? <Button variant="outlined" onClick={clearAllMitFokus}>Clear all filters</Button>
            : undefined
        }
        onRetry={leerGrund === 'error' ? fetchAll : undefined}
        detail={leerGrund === 'error' ? error : undefined}
      />
    </Box>
  ) : null

  const tabelle = (
    <Box ref={tabelleRef} data-testid="liste-tabelle" sx={{ mt: SP.tight }}>
      <Panel pad="none" clip>
        <TableViewport
          footerPadPx={0}
          priorities={SPALTEN}
          containerWidth={messBasis}
          stickyFirstColumn
          edgeHint
        >
          {/* 🔴 Nachbesserung 7: `minWidth` 780 → 950. Die sechs Spalten
              brauchen zusammen 950 px (126 + 232 + 120 + 136 + 150 + 186
              Entität). Der Tausch ist NICHT „Entität auf 34 px quetschen" —
              das galt am alten Minimum 780. Er lautet: unter 950 px Behälter
              (Fenster ≈ 1108–1277 px) rollt `TableViewport` waagerecht, statt
              dass die Entität unter ihr Minimum fällt (bei 872 px Behälter
              wären es 108 px) und `PO — goods receipt (AVIS)` (146 px) auf
              zwei Zeilen umbricht. Der Rollfall ist der gebaute Weg
              (`edgeHint`, `stickyFirstColumn`), kein Notbehelf.
              Nachbesserung 8 (2026-10-27) hat die 108 der Kennung durch 126
              ersetzt und die 18 px der Entität genommen: die Summe bleibt
              950, und die Entität behält bei 950 noch 154 px Inhalt — 8 px
              mehr, als `PO — goods receipt (AVIS)` (146 px) braucht. */}
          <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 950, maxWidth: tabellenMax }}>
            <TableHead>
              <TableRow>
                <TableCell sx={spaltenBreite('entity')}>Entity</TableCell>
                <TableCell sx={spaltenBreite('identifier')}>Identifier</TableCell>
                <TableCell sx={{ width: zustandSpalte }}>Next poll / last state</TableCell>
                <TableCell align="right" sx={spaltenBreite('polls')}>Polls / errors</TableCell>
                <TableCell sx={spaltenBreite('status')}>Status</TableCell>
                <TableCell align="right" sx={spaltenBreite('actions')}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seitenZeilen.map((w) => {
                const entityLink = entityHref(w)
                return (
                  <TableRow key={w.id} hover>
                    <TableCell sx={spaltenBreite('entity')}>
                      {entityLink
                        ? <MuiLink href={entityLink} target="_blank" rel="noopener">{ENTITY_LABELS[w.entity_type] || w.entity_type}</MuiLink>
                        : (ENTITY_LABELS[w.entity_type] || w.entity_type)}
                    </TableCell>
                    <TableCell sx={KENNUNG_SX}><code>{w.lsm_identifier}</code></TableCell>
                    <TableCell sx={{ width: zustandSpalte }}>
                      {/* 🔴 G13a-2 Nachbesserung 4 (Regel 15 W3 / Regel 16 W2):
                          der letzte Zustand stand bis hier im Tooltip dieses
                          Werts und war damit nur mit der Maus erreichbar — auf
                          Touch-Geräten und mit der Tastatur gar nicht
                          (WCAG 2.1.1). Er ist jetzt die zweite, kleine Zeile
                          der Zelle, in der Hausform der Fristzelle aus
                          `rma/RMAFaelligkeit` (Wert oben, `caption` darunter).
                          Die Spalte „Last state" ist entfallen; sie war
                          optional (bei 1440 unsichtbar) und hätte den Wert bei
                          ≥1920 ein zweites Mal gezeigt.
                          🔴 Nachbesserung 5 (Regel 16 C2 / W-1): das Etikett
                          „Last state" steht jetzt im Spaltenkopf — in der
                          108-px-Zelle brach es auf eine dritte Zeile (s.
                          `SPALTEN`). Die `caption` trägt nur den Wert.
                          🔴 Nachbesserung 6 (Regel 16 C3): der Wert ist die
                          ECHTE Form (s. `stateText`) und wird mit `noWrap` auf
                          EINE Zeile gehalten — sonst wäre die Zelle bei jeder
                          echten RT-Zeile wieder zwei- bis dreizeilig.
                          🔴 Nachbesserung 7 (2026-10-26): die Spalte ist so
                          breit, dass die drei echten Erzeugerformen
                          vollständig stehen (`zustandBreite`: 316 px Inhalt bei
                          1440, 200 px bei 1280). Die Ellipse bleibt die
                          Notbremse für UNBEKANNTE Schlüssel; `textContent`
                          bleibt in jedem Fall vollständig.
                          🔴 Regel 15 W3 / Regel 16 W-2: der entfernte Tooltip
                          trug AUCH den absoluten Poll-Zeitpunkt. Er bleibt
                          **maschinenlesbar** (`<time dateTime>`, kein
                          Screenreader-Vortrag — W-7), ohne Hover; sichtbar
                          steht die relative Zeit. */}
                      <Stack>
                        {/* 🔴 Regel 16 W-8: `<time>` OHNE `dateTime` ist kein
                            gültiges `<time>` (der Elementinhalt müsste dann
                            maschinenlesbar sein, hier ist er „—"). Ohne
                            Zeitpunkt ist der Träger deshalb ein gewöhnlicher
                            Text. */}
                        <Typography
                          variant="body2"
                          component={w.next_poll_at ? 'time' : undefined}
                          dateTime={w.next_poll_at || undefined}
                        >
                          {fmtRelative(w.next_poll_at)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          sx={{ minWidth: 0 }}
                        >
                          {stateText(w)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={spaltenBreite('polls')}>
                      {w.poll_count}
                      {w.consecutive_errors > 0 && (
                        <Typography component="span" color="error" sx={{ ml: SP.chip, fontSize: FS.xs }}>
                          /{w.consecutive_errors}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={spaltenBreite('status')}>{statusChip(w)}</TableCell>
                    <TableCell align="right" sx={spaltenBreite('actions')}>{zeilenAktion(w)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableViewport>
        {/* Der Leerzustand steht INNERHALB der Tabelle-Fläche, UNTER dem Kopf:
            die Kopfzellen bleiben an ihrem Platz (P33), und es gibt genau EIN
            `[data-reason]`. */}
        {leerGrund && leerzustand}
      </Panel>
    </Box>
  )

  return (
    <PageContainer width="wide">
      {/* 🔴 K-11 (UI-Durchgang 2 Teil 6, 2026-10-10): „Backfill RT watches" ist
          eine SEITENWEITE Handlung und stand in der Filterzeile — sie gehoert in
          den Kopf (`actions`). Das Kopfaktionsmodell sagt: Knöpfe gehoeren in
          `actions`/`overflow`, die Zeile darunter traegt Filter und Auffrischen.
          Der Kopf traegt hier GENAU EINE Aktion, deshalb rendert er sie ohne
          weiteren Eintrag als `contained` — und das ist richtig: der Backfill
          ist die eine Handlung, die diese Seite veranlasst, und sie ist nicht
          destruktiv (`pattern-library.md` §Kopfaktionsmodell Nr. 3). Die Probe:
          `frontend/tests/design/ui2-k-11-kopfaktionen.spec.ts`.
          🔴 Der „Refresh" daneben BLEIBT in der Zeile (W13/E8, 2026-10-07): ein
          Auffrischen ist nie die Primaeraktion und gehoert dorthin, wo die Daten
          liegen. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          title="LSM Smart Watcher"
          subtitle="Adaptive polling for RT + DN + AVIS"
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Ops' }, { label: 'LSM Watcher' }]}
          actions={[
            {
              key: 'backfill-rt',
              label: busy.__backfill ? 'Backfilling…' : 'Backfill RT watches',
              emphasis: 'primary',
              onClick: handleBackfillRt,
              disabled: busy.__backfill,
            },
          ]}
        />
      </Box>

      {/* Der Kopf-Alert traegt NUR Aktionsfehler (Backfill, Sync now, Reset) und
          Info. Ein LADEFEHLER gehoert in den Bereich, dessen Daten fehlen —
          er steht unten im Panel, genau einmal. */}
      {aktionsFehler && (
        <Alert severity="error" sx={{ mb: SP.card }} onClose={() => setAktionsFehler(null)}>
          {aktionsFehler}
        </Alert>
      )}
      {info && <Alert severity="info" sx={{ mb: SP.card }} onClose={() => setInfo(null)}>{info}</Alert>}

      {loading && !status ? (
        /* 🔴 G13a-2 Nachbesserung 3 (Regel 15 B1): das Geruest trägt die FORM der
           GELADENEN Fläche. Bis hierher stand `KpiCardsSkeleton … paper`: vier
           umrandete `Paper`-Kacheln mit 12-px-Radius. Geladen ist die Reihe eine
           randlose Zahlenreihe mit gestrichelten Trennern (`KpiRow`/`StatTile`) —
           beim Eintreffen der Daten verschwanden vier Karten und wurden zu einer
           Reihe, genau der Sprung, den das Geruest verhindern soll. Jetzt rendert
           das Geruest `KpiRow` mit vier `StatTile … loading`, also dieselbe Form.
           `role="status"` + Name und `data-skelett-kachel` je Kachel bleiben: sie
           sind die Anker der Form-Probe (`tests/design/skelett-anatomie.spec.ts`,
           Fall S.1 `/ops/lsm-watcher`). Die vier Beschriftungen sind statische
           Literale des Aufrufers (kein erfundener Text); `hint` wird gesetzt,
           damit der Hinweis-Slot schon im Ladezustand reserviert ist. */
        <Box role="status" aria-label="Loading watcher status" aria-busy="true">
          <KpiRow maxMobile={4}>
            <Box data-skelett-kachel=""><StatTile label="Active" hint="polling now" loading /></Box>
            <Box data-skelett-kachel=""><StatTile label="Overdue" hint="past due" loading /></Box>
            <Box data-skelett-kachel=""><StatTile label="In backoff" hint="retrying" loading /></Box>
            <Box data-skelett-kachel=""><StatTile label="Disabled (recoverable)" hint="resettable" loading /></Box>
          </KpiRow>
        </Box>
      ) : (
        <Stack gap={SP.card}>
          {status && !status.enabled && (
            <Alert severity="warning" variant="outlined">
              Watcher is <b>OFF</b> (env <code>LSM_WATCHER_ENABLED</code> not set).
              Watches accumulate but nothing is polled.
            </Alert>
          )}

          {/* ── Zone KPI — vier Kacheln, kein Klickziel (C9-1, P29) ───────── */}
          <Box data-testid="liste-kpi">
            <KpiRow maxMobile={4}>
              <StatTile
                label="Active"
                value={zahl(status?.active)}
                hint={kachelHinweis('polling now', zahl(status?.active))}
                tone={status ? 'ok' : 'plain'}
              />
              <StatTile
                label="Overdue"
                value={zaehler?.overdue ?? null}
                hint={kachelHinweis('past due', zaehler?.overdue)}
                tone={(zaehler?.overdue ?? 0) > 0 ? 'warn' : 'plain'}
              />
              <StatTile
                label="In backoff"
                value={zaehler?.backoff ?? null}
                hint={kachelHinweis('retrying', zaehler?.backoff)}
                tone={(zaehler?.backoff ?? 0) > 0 ? 'warn' : 'plain'}
              />
              {/* Beide Altlast-Zaehler ZUSAMMEN mit `entity_gone` — genau die
                  Menge, die der Reset-Knopf annimmt (`RESETTABLE`). Getrennt zu
                  zaehlen haette den 11.08. nur verschoben: `disabled_html_error`
                  lieferte /status seit 2026-05-29 und wurde nie gerendert.

                  Nicht `tone="err"`: die Zeilen sind ueber den Reset-Knopf
                  wiederherstellbar, und ein dauerhaftes Rot, das von selbst nie
                  faellt, wird zu Tapete. */}
              <StatTile
                label="Disabled (recoverable)"
                value={zaehler?.circuit ?? null}
                hint={kachelHinweis('resettable', zaehler?.circuit)}
                tone={(zaehler?.circuit ?? 0) > 0 ? 'warn' : 'plain'}
              />
            </KpiRow>
          </Box>

          {/* ── Daten-Panel: Leiste, Tabelle, Fußzeile in EINER Fläche ────── */}
          <Box data-testid="liste-panel" aria-busy={loading || undefined}>
            {/* 🔴 G13a-2 Nachbesserung 2 — EIN Ladefehler, EINE Meldung.
                Ist der Aufbau gescheitert (`sperre`), traegt der Leerzustand
                darunter „Could not load the watcher." + „Try again" + die
                Ursache als Detail; ein zusaetzlicher Alert hier sagte
                denselben Ausfall ein zweites Mal an (das Foto vom 2026-10-24
                zeigte ihn dreimal, mit dem Kopf-Alert). Der Alert steht
                deshalb nur fuer den Fall, den der Leerzustand nicht abdeckt:
                ein gescheitertes NACH-LADEN, waehrend Daten stehen. Dort ist
                er die EINZIGE Live-Region (`role="alert"` aus MUI) — das
                frueher hier gesetzte `role="none"` schwieg ihn tot, weil es
                einen zweiten Alert daneben gab; den gibt es nicht mehr. */}
            {error && !sperre && (
              <Alert severity="error" sx={{ mb: SP.card }}>{error}</Alert>
            )}

            {/* 🔴 G13a-2 Nachbesserung 2: der Auffrisch-Knopf traegt KEIN
                eigenes `aria-label` mehr. Es hiess „Refresh watcher list",
                waehrend sichtbar „Refresh" steht — WCAG 2.5.3 (Label in Name)
                verlangt den sichtbaren Text im zugaenglichen Namen; keine Probe
                brauchte den laengeren Namen (Grep `frontend/tests`). Der Name
                ist jetzt der sichtbare Text. */}
            <ListToolbar
              quickFilters={quickReihe}
              count={leerGrund ? undefined : {
                noun: 'watches',
                n: gefiltert.length,
                m: gesamt,
              }}
              refresh={(
                <Button size="small" startIcon={<RefreshIcon />} onClick={fetchAll}>
                  Refresh
                </Button>
              )}
              query={{ chips, clearAll: clearAllMitFokus }}
            >
              {query.ignoredValues.length > 0 && (
                <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mx: SP.card, mt: SP.tight }}>
                  {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
                </Alert>
              )}

              {/* N9.3: die Reihenfolge haengt nicht an einer Spalte (Server
                  `next_poll_at asc`), also kein `aria-sort` — der Satz ist der
                  einzige Traeger der Sortierung (C9-7).
                  🔴 G13a-2 Nachbesserung 3 (Regel 16 W7): der Satz steht nur
                  mit Zeilen — dieselbe Bedingung wie die Fußzeile. Ueber einem
                  Leerzustand war er eine Aussage ueber eine Liste, die es nicht
                  gibt. */}
              {!leerGrund && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="sortiert-nach"
                  sx={{ display: 'block', mt: SP.tight }}
                >
                  Sorted by next poll (soonest first).
                </Typography>
              )}

              {/* Der Kappungshinweis (C9-4). „Narrow with a filter" waere hier
                  eine FALSCHE Anweisung: die Filter laufen clientseitig ueber
                  die geladenen 200 und laden keine Zeile nach. */}
              {gekappt && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid="kappung-hinweis"
                  sx={{ display: 'block', mt: SP.chip }}
                >
                  {`Showing the first ${KAPPUNG} watches (sorted by next poll); filters apply to those ${KAPPUNG}.`}
                </Typography>
              )}

              {watches === null && loading && (
                <Box data-testid="liste-skelett" sx={{ mt: SP.tight }}>
                  <TableSkeleton columns={6} rows={5} ariaLabel="Loading watches" />
                </Box>
              )}

              {/* Desktop trägt die Tabelle, das Telefon die reduzierten Zeilen
                  (C9-6) — GEGENSTÜCKE, nicht beides gleichzeitig (dieselbe
                  Bauform wie die Musterroute). */}
              {!mobil && tabelle}

              {mobil && (leerGrund ? leerzustand : (
                <Box data-testid="liste-karte" sx={{ mt: SP.tight }}>
                  <Stack spacing={SP.tight}>
                    {seitenZeilen.map((w) => {
                      // 🔴 G13a-2 Nachbesserung 3 (Regel 16 B1 + W4): der TITEL
                      // ist die Kennung und der Weg in den Fall. Vorher war der
                      // Titel die Konstante „RMA — return (RT)" (13x derselbe)
                      // und die Kennung der `id`-Slot — auf dem Telefon fuehrte
                      // damit KEIN Weg in den Fall: die einzige Zeilenaktion ist
                      // bei einem aktiven Watch „Sync now", und die Basis hatte
                      // dort die Tabelle mit Entitaetslink. Der Titel ist jetzt
                      // der Link (`MobileRecordCard` nennt ihn „den Datensatz
                      // und seinen Weg hinein"); die Aktionsspalte bleibt genau
                      // eine Handlung (C9-6/P19), weil der Link kein Knopf ist.
                      // Nachtrag zu Plan C9-6 vom 2026-10-24.
                      //
                      // 🔴 Nachbesserung 4 (Regel 16 W1 / Regel 15 W5): die
                      // Entitätsbeschriftung liegt NICHT mehr im `id`-Slot. Der
                      // ist im geteilten Baustein der Slot der KENNUNG
                      // (`MobileRecordCard.jsx`: monospace, `nowrap`, nie
                      // gekürzt) — Prosa in Schreibmaschinenschrift, und sie
                      // lief ohne Trenner in die relative Zeit („… (RT) in
                      // 26366d"). Jetzt ist `id` leer (die Kennung ist der
                      // Titel) und die Beschriftung steht als PRÄFIX der
                      // `meta`-Zeile, mit „ · " getrennt.
                      const href = entityHref(w)
                      return (
                        <MobileRecordCard
                          key={w.id}
                          variant="row"
                          title={href
                            ? <MuiLink href={href} target="_blank" rel="noopener">{w.lsm_identifier}</MuiLink>
                            : <code>{w.lsm_identifier}</code>}
                          chips={statusChip(w)}
                          meta={(
                            <Typography variant="caption" color="text.secondary">
                              {ENTITY_LABELS[w.entity_type] || w.entity_type}
                              {' · '}
                              {/* 🔴 Nachbesserung 6 (Regel 16 C3/W-6): der
                                  absolute Poll-Zeitpunkt ist auch auf dem
                                  Telefon maschinenlesbar — dieselbe Form wie in
                                  der Tabelle, und ohne Zeitpunkt KEIN `<time>`
                                  (W-8). */}
                              {w.next_poll_at
                                ? <time dateTime={w.next_poll_at}>{fmtRelative(w.next_poll_at)}</time>
                                : fmtRelative(w.next_poll_at)}
                              {` · ${w.poll_count}/${w.consecutive_errors || 0} · ${stateText(w)}`}
                            </Typography>
                          )}
                          actions={zeilenAktion(w)}
                        />
                      )
                    })}
                  </Stack>
                </Box>
              ))}

              {/* Die Fußzeile steht nur mit Inhalt (N8.7) — im Leerzustand gibt
                  es sie nicht, und ihre Marke entsteht dann auch nicht. */}
              {!leerGrund && gefiltert.length > 0 && (
                <Box data-testid="liste-fuss">
                  <TableFooterSimple
                    count={gefiltert.length}
                    page={seite}
                    onPageChange={(p) => set('page', p)}
                    rowsPerPage={SEITE}
                    stickyOnMobile={false}
                    maxWidth={tabellenMax}
                  />
                </Box>
              )}
            </ListToolbar>
          </Box>

          {status?.recent_events?.length > 0 && (
            <Panel>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Recent events
              </Typography>
              <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Watch</TableCell>
                      <TableCell>Changed?</TableCell>
                      <TableCell>New status</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {status.recent_events.map((e, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{fmtRelative(e.polled_at)}</TableCell>
                        <TableCell>#{e.watch_id}</TableCell>
                        <TableCell>{e.transitioned ? '✓' : ''}</TableCell>
                        <TableCell>{e.new_case_status || '—'}</TableCell>
                        <TableCell>{e.duration_ms ? `${e.duration_ms} ms` : '—'}</TableCell>
                        <TableCell sx={{ color: 'error.main', fontSize: FS.xs }}>
                          {e.error_text || ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Panel>
          )}
        </Stack>
      )}
    </PageContainer>
  )
}
